import {TicketStatusCategory} from '@frontapp/ticketing';
import {compact} from 'lodash';
import React, {FC, useMemo} from 'react';
import {CellProps, Column} from 'react-table';
import styled from 'styled-components';

import {findIndicatedTicketStatus} from '../../../../../libs/inboxes/business/src/ticketing/conversation';
import {ticketStatusCategoryNames} from '../../../../../libs/inboxes/ui/src/ticketing/ticketStatus';
import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {fromEntries} from '../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {ConversationTeamStatusesEnum} from '../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {buildTeammateDisplayName} from '../../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {reportToBugsnag} from '../../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {getTicketingPreferences} from '../../../../../libs/shared/core/src/store/data/company/commonCompanySelectors';
import {AppContentTypesEnum} from '../../../../../libs/shared/core/src/store/panels/app/commonAppPanelState';
import {AnalyticsPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {TeammateAvatarWithStatus} from '../../../../components/common/avatars/teammateAvatarWithStatus';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {buildContactDisplayName} from '../../../../helpers/models/contactHelpers';
import {ConversationModel} from '../../../../models/api/conversationModel';
import {makeGetVisualRecipient} from '../../../../store/data/contacts/recipientsSelectors';
import {makeGetConversationById} from '../../../../store/data/conversations/conversationsSelectors';
import {makeGetConversationSubject} from '../../../../store/data/conversations/conversationSubjectSelectors';
import {getAccounts} from '../../../../store/data/dataSelectors';
import {
  getOnlineTeammateIdsSet,
  TeammateOnlineStatusesEnum,
} from '../../../../store/data/teammates/onlineTeammatesSelectors';
import {getFindTeammate, getUserId} from '../../../../store/data/teammates/teammatesSelectors';
import {pushParametersAndFocusAction} from '../../../../store/panels/app/appPanelRouteActions';
import {popoutConversationViewerAction} from '../../../../store/panels/viewer/viewerPanelActions';
import {navigateToConversation} from '../../../../store/stateless/navigationActions';
import {renderDurationValue} from '../../../helpers/metricRenderers/durationMetricRenderer';
import {ReportElementIdsEnum} from '../../../helpers/reportHelpers/reportTypes';
import {ViewInInbox, ViewInInboxLinkPositionsEnum} from '../viewInInbox';
import {makeGetOptionalInboxesParametersForConversation} from './selectors';

/*
 * Styles.
 */

const StyledTeammateRowDiv = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
`;

const StyledSubjectSpan = styled.span`
  ${ellipsis()}
`;

const StyledTextAlignRightDiv = styled.div`
  text-align: right;
`;

const StyledGreyDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
`;

/*
 * Components.
 */

const ConversationSubjectCellWithConversation: FC<{
  conversation: ConversationModel;
  shouldPopoutConversation: boolean;
  panelId: AnalyticsPanelId;
}> = ({conversation, shouldPopoutConversation, panelId}) => {
  const getConversationSubject = useMemo(
    () => makeGetConversationSubject<void>(() => conversation, getUserId),
    [conversation],
  );

  const getOptionalInboxesParametersForConversation = useMemo(
    () =>
      makeGetOptionalInboxesParametersForConversation<void>(
        () => conversation,
        () => panelId,
      ),
    [conversation, panelId],
  );

  const conversationSubject = useAppSelector(getConversationSubject);

  const inboxesParameters = useAppSelector(getOptionalInboxesParametersForConversation);

  const actionBuilder = () => {
    if (!inboxesParameters) {
      // This shouldn't happen since the Live dashboard only cares about team conversations.
      reportToBugsnag(new Error(`Could not build inbox parameters for conversation ${conversation.id}.`));

      // We should still open the conversation.

      if (shouldPopoutConversation) {
        return popoutConversationViewerAction(conversation.id);
      }

      return navigateToConversation(conversation.id);
    }

    if (shouldPopoutConversation) {
      return popoutConversationViewerAction(conversation.id, undefined, undefined, inboxesParameters);
    }

    return pushParametersAndFocusAction(panelId, {
      contentType: AppContentTypesEnum.INBOXES,
      parameters: inboxesParameters,
    });
  };

  return (
    <ViewInInbox
      actionBuilder={actionBuilder}
      trackingPayload={{
        report_section: ReportElementIdsEnum.LIVE_DASHBOARD_OLDEST_OPEN_CONVERSATIONS_TABLE,
        resource_type: 'conversation',
        resource_id: conversation.id,
        clicked_value: 'subject',
      }}
      linkSide={ViewInInboxLinkPositionsEnum.RIGHT}
    >
      <StyledSubjectSpan>{conversationSubject.text}</StyledSubjectSpan>
    </ViewInInbox>
  );
};

const ConversationSubjectCell: FC<{
  conversationId: number;
  shouldPopoutConversation: boolean;
  panelId: AnalyticsPanelId;
}> = ({conversationId, shouldPopoutConversation, panelId}) => {
  const getConversation = useMemo(() => makeGetConversationById(conversationId), [conversationId]);
  const conversation = useAppSelector(getConversation);

  if (!conversation) {
    return '-';
  }

  return (
    <ConversationSubjectCellWithConversation
      conversation={conversation}
      shouldPopoutConversation={shouldPopoutConversation}
      panelId={panelId}
    />
  );
};

function makeSubjectColumn(
  shouldPopoutConversation: boolean,
  panelId: AnalyticsPanelId,
): ConversationIdColumnType {
  const Cell: FC<CellProps<{conversation_id: number}>> = ({value}) => (
    <ConversationSubjectCell
      conversationId={value}
      shouldPopoutConversation={shouldPopoutConversation}
      panelId={panelId}
    />
  );

  return {
    Header: <Trans id="live-dashboard-oldest-open-subject-column">Subject</Trans>,
    id: 'subject',
    accessor: 'conversation_id',
    minWidth: 190,
    maxWidth: 190,
    width: '100%',
    Cell,
  };
}

const ConversationOpenForCell: FC<{value: number}> = ({value}) => {
  const getConversation = useMemo(() => makeGetConversationById(value), [value]);
  const conversation = useAppSelector(getConversation);

  const durationSeconds = conversation
    ? (new Date().getTime() - conversation.orders.waiting_since) / 1_000
    : 0;

  return (
    <StyledTextAlignRightDiv>
      {renderDurationValue({
        value: durationSeconds,
        isCompact: true,
      })}
    </StyledTextAlignRightDiv>
  );
};

const lastMessageColumn = {
  Header: <Trans id="live-dashboard-oldest-open-last-message-column">Last message</Trans>,
  id: 'waiting_since',
  accessor: 'conversation_id',
  width: 102,
  Cell: ConversationOpenForCell,
} as const;

const ConversationContactCell: FC<{value: number}> = ({value}) => {
  const getConversation = useMemo(() => makeGetConversationById(value), [value]);
  const getRecipient = useMemo(() => makeGetVisualRecipient(getConversation, getUserId), [getConversation]);
  const recipient = useAppSelector(getRecipient);

  if (!recipient) {
    return '-';
  }

  return buildContactDisplayName(recipient);
};

const contactColumn = {
  Header: <Trans id="live-dashboard-oldest-open-contact-column">Contact</Trans>,
  id: 'contact',
  accessor: 'conversation_id',
  minWidth: 160,
  maxWidth: 220,
  Cell: ConversationContactCell,
} as const;

const ConversationAccountCell: FC<{value: number}> = ({value}) => {
  const getConversation = useMemo(() => makeGetConversationById(value), [value]);
  const getRecipient = useMemo(() => makeGetVisualRecipient(getConversation, getUserId), [getConversation]);

  const recipient = useAppSelector(getRecipient);
  const accounts = useAppSelector(getAccounts);

  const accountId = recipient?.card?.accountId;
  const account = accountId ? accounts[accountId] : undefined;

  if (!account) {
    return '-';
  }

  return account.name;
};

const accountColumn = {
  Header: <Trans id="live-dashboard-oldest-open-account-column">Account</Trans>,
  id: 'account',
  accessor: 'conversation_id',
  minWidth: 160,
  maxWidth: 220,
  Cell: ConversationAccountCell,
} as const;

const ConversationAssigneeCell: FC<{value: number}> = ({value}) => {
  const getConversation = useMemo(() => makeGetConversationById(value), [value]);
  const conversation = useAppSelector(getConversation);

  const findTeammate = useAppSelector(getFindTeammate);
  const onlineTeammateIds = useAppSelector(getOnlineTeammateIdsSet);

  if (!conversation) {
    return '-';
  }

  const {assigneeId} = conversation;

  if (!assigneeId) {
    return (
      <StyledGreyDiv>
        <StyledTeammateRowDiv>
          <Icon name="none" />
          <Trans id="live-dashboard-open-conversation-unassigned">Unassigned</Trans>
        </StyledTeammateRowDiv>
      </StyledGreyDiv>
    );
  }

  const teammate = findTeammate(assigneeId);

  // Should not happen, but better safe than sorry.
  if (!teammate) {
    return (
      <em>
        <Trans id="live-dashboard-open-conversation-assigned">Assigned</Trans>
      </em>
    );
  }

  const isOnline = onlineTeammateIds?.has(assigneeId) ?? false;

  return (
    <StyledTeammateRowDiv>
      <TeammateAvatarWithStatus
        teammate={teammate}
        size={VisualSizesEnum.MEDIUM}
        onlineStatus={isOnline ? TeammateOnlineStatusesEnum.ONLINE : TeammateOnlineStatusesEnum.OFFLINE}
        teammateStatus={teammate.availabilityStatus}
      />
      {buildTeammateDisplayName(teammate)}
    </StyledTeammateRowDiv>
  );
};

const assigneeColumn = {
  Header: <Trans id="live-dashboard-oldest-open-assignee-column">Assignee</Trans>,
  id: 'assignee',
  accessor: 'conversation_id',
  minWidth: 160,
  maxWidth: 220,
  Cell: ConversationAssigneeCell,
} as const;

const ConversationStatusCell: FC<{value: number}> = ({value}) => {
  const getConversation = useMemo(() => makeGetConversationById(value), [value]);
  const conversation = useAppSelector(getConversation);
  const ticketingPreferences = useAppSelector(getTicketingPreferences);

  if (!conversation) {
    return '-';
  }

  const ticketStatus = findIndicatedTicketStatus(ticketingPreferences, conversation);

  // FIXME(ldemarne): Ideally the following would be handled by `findIndicatedTicketStatus`.
  // Unfortunately, when ticketing is not provisioned, it bails out, when we still offer waiting stats in the form of snoozed conversations.
  const defaultTicketStatusCategory =
    conversation.teamStatus === ConversationTeamStatusesEnum.OPEN
      ? TicketStatusCategory.OPEN
      : TicketStatusCategory.WAITING;

  const ticketStatusCategory = ticketStatus?.category ?? defaultTicketStatusCategory;

  return ticketStatusCategoryNames[ticketStatusCategory];
};

const statusColumn = {
  Header: <Trans id="live-dashboard-oldest-conversations-status-column">Status</Trans>,
  id: 'status',
  accessor: 'conversation_id',
  width: 72,
  Cell: ConversationStatusCell,
} as const;

const hideableColumns = [assigneeColumn, contactColumn, accountColumn] as const;

export const liveDashboardHideableConversationColumnIds = hideableColumns.map((column) => column.id);
export type LiveDashboardHideableConversationColumnId =
  (typeof liveDashboardHideableConversationColumnIds)[number];

export const liveDashboardHideableConversationColumnNames = fromEntries(
  hideableColumns.map((column) => [column.id, column.Header]),
);
export const liveDashboardConversationColumnsDefaultHiddenIds = [accountColumn.id];

type ConversationIdColumnType = Column<{conversation_id: number}>;

interface LiveDashboardOldestConversationsColumnsOptions {
  isPoppedOut: boolean;
  hasWaitingMetrics: boolean;
  panelId: AnalyticsPanelId;
}

export function makeLiveDashboardOldestConversationsColumns(
  options: LiveDashboardOldestConversationsColumnsOptions,
): Array<ConversationIdColumnType> {
  const {isPoppedOut, hasWaitingMetrics, panelId} = options;

  return compact([
    // When the Live dashboard itself is popped out, redirect the main app to the conversation.
    makeSubjectColumn(!isPoppedOut, panelId),

    // Only include the status column when the table may contain Waiting conversations.
    hasWaitingMetrics ? statusColumn : undefined,

    lastMessageColumn,
    ...hideableColumns,
  ]);
}
